/**
 * Purpose: Orchestration layer for the Owner Dashboard — fetch, permission
 *          check, module resolution, and active-module navigation. The page
 *          composes; this hook orchestrates; services own the I/O.
 * Inputs: slugOrId (URL segment), moduleId (URL segment, optional).
 * Outputs: { business, loading, error, canManage, modules, navItems, activeId,
 *            selectModule, ctx, reload }.
 *
 * Phase 5.5 boundary (noted, not acted on): the fetch + normalize + derived
 * counts below are the same reads useBusinessDetail performs. Both should later
 * call a `businessService`-backed domain service (or a shared query cache)
 * instead of each hook owning its own fetch. Recorded in decisions.md as the
 * first natural extraction point — deliberately NOT refactored during Phase 5.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { businessService } from '../services/api';
import { normalizeCatalogueList } from '../services/businessService';
import { canManageOwnedResource } from '../utils/resourcePermissions';
import { resolveDashboardModules, getDefaultDashboardModuleId } from '../config/dashboardModules';
import { getBusinessMedia } from '../utils/media';
import { getBusinessContacts } from '../utils/contacts';

function useOwnerDashboard(slugOrId, moduleId = null) {
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(!!slugOrId);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);

  const reload = useCallback(async () => {
    if (!slugOrId) return;
    try {
      setError(null);
      const data = await businessService.getBusiness(slugOrId);
      setBusiness(data);
      setProducts(normalizeCatalogueList(data.products));
      setServices(normalizeCatalogueList(data.services));
    } catch (err) {
      console.error('Error loading dashboard business:', err);
      setError('Failed to load your business');
      setBusiness(null);
    } finally {
      setLoading(false);
    }
  }, [slugOrId]);

  useEffect(() => {
    if (!slugOrId) return;
    setLoading(true);
    reload();
  }, [slugOrId, reload]);

  // Lift an updated business from a settings save so every module (Overview
  // stats, header) reflects the change without a refetch. Optimistic + shared.
  const applyBusiness = useCallback((updated) => {
    if (updated && updated.id) setBusiness(updated);
  }, []);

  const canManage = !!(user && business && canManageOwnedResource(user, business));

  const ctx = useMemo(
    () => ({
      business,
      data: {
        products: products.filter((p) => !p.is_menu_item),
        menuItems: products.filter((p) => p.is_menu_item),
        services,
        galleryMedia: getBusinessMedia(business),
        contacts: getBusinessContacts(business),
        teamMembers: Array.isArray(business?.team_members) ? business.team_members : [],
        reviews: [],
        views: business?.views || 0,
      },
    }),
    [business, products, services],
  );

  const modules = useMemo(() => (canManage ? resolveDashboardModules(ctx) : []), [canManage, ctx]);

  const navItems = useMemo(
    () => modules.map((m) => ({ id: m.id, label: m.title(ctx), icon: m.icon, badge: m.badge?.(ctx) })),
    [modules, ctx],
  );

  // The URL is the source of truth for the active module; fall back to the first
  // resolved module when the segment is absent or names an unavailable module.
  const activeId = useMemo(() => {
    const requested = modules.find((m) => m.id === moduleId);
    return requested ? requested.id : getDefaultDashboardModuleId(modules);
  }, [modules, moduleId]);

  return {
    business,
    loading,
    error,
    canManage,
    modules,
    navItems,
    activeId,
    ctx,
    reload,
    applyBusiness,
  };
}

export default useOwnerDashboard;
